import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 116 Backoffice Abnormal Heartbeat API
// (BackofficeAbnormalController/AbnormalHeartbeatService). Same base/auth
// convention as ../tracking/trackingApi.js and ../footsteps/footstepsApi.js -
// apiClient.get/post with no `base` override, the same Bearer-session-
// authenticated backend every other backoffice controller uses. This file
// never calls a geofence/fake-location/face-recognition/SOS API (none of
// those exist anywhere in this codebase), and it never sends a real
// notification - every write here maps 1:1 to one of the five real P116
// routes below.

const ABNORMAL_PATH = "/backoffice/abnormal";

// Exactly the query param names BackofficeAbnormalController.ListEvents
// declares, in the same order as the controller signature - anything else
// passed to listAbnormalEvents is silently dropped rather than forwarded, so
// a typo'd key can never accidentally reach the backend as an unfiltered
// pass-through. Scope-isolated visibility always happens on the backend from
// the caller's own session, never from a value this file sends.
const EVENTS_LIST_PARAM_KEYS = [
  "search",
  "abnormalType",
  "severity",
  "status",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "agentId",
  "employerId",
  "workerId",
  "page",
  "pageSize",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listAbnormalEvents(params = {}) {
  return apiClient.get(`${ABNORMAL_PATH}/events${buildQuery(EVENTS_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty abnormalEventId - this file never
// issues a request for a missing/blank id (the caller only calls this once a
// row/card is actually clicked).
export function getAbnormalEventDetail(abnormalEventId) {
  if (!abnormalEventId) {
    throw new Error("getAbnormalEventDetail requires an abnormalEventId");
  }
  return apiClient.get(`${ABNORMAL_PATH}/events/${encodeURIComponent(abnormalEventId)}`);
}

// SuperAdmin-only manual scan (AbnormalHeartbeatService.ScanForMissingHeartbeatAsync).
// nowUtc is optional - omitted entirely when not provided, never sent as an
// empty string (the backend's own DateTime? parses null differently from "").
export function runHeartbeatScan({ nowUtc, dryRun } = {}) {
  const body = { dryRun: Boolean(dryRun) };
  if (nowUtc) {
    body.nowUtc = nowUtc;
  }
  return apiClient.post(`${ABNORMAL_PATH}/heartbeat-scan`, body);
}

export function acknowledgeAbnormalEvent(abnormalEventId, { note } = {}) {
  if (!abnormalEventId) {
    throw new Error("acknowledgeAbnormalEvent requires an abnormalEventId");
  }
  return apiClient.post(`${ABNORMAL_PATH}/events/${encodeURIComponent(abnormalEventId)}/acknowledge`, { note: note || null });
}

export function resolveAbnormalEvent(abnormalEventId, { resolutionNote } = {}) {
  if (!abnormalEventId) {
    throw new Error("resolveAbnormalEvent requires an abnormalEventId");
  }
  return apiClient.post(`${ABNORMAL_PATH}/events/${encodeURIComponent(abnormalEventId)}/resolve`, { resolutionNote: resolutionNote || null });
}
